import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [loading, setLoading] = useState(true);

  const fetchUserPlan = useCallback(async (userId) => {
    if (!supabase) return;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('plan, plan_hasta')
        .eq('id', userId)
        .single();
      let plan = data?.plan || 'free';
      // Renovación cancelada: el acceso Pro dura solo hasta plan_hasta
      if (plan === 'pro' && data?.plan_hasta && new Date(data.plan_hasta) < new Date()) plan = 'free';
      setUserPlan(plan);
      sessionStorage.setItem('kin_plan', plan);
    } catch {
      setUserPlan('free');
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchUserPlan(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchUserPlan(session.user.id);
        } else {
          setUserPlan('free');
          sessionStorage.removeItem('kin_plan');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchUserPlan]);

  const signUp = async (email, password) => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } };
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (!error && data.user) {
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email,
        plan: 'free',
      });
    }
    return { data, error };
  };

  const signIn = async (email, password) => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } };
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signInWithGoogle = async () => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } };
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUserPlan('free');
    sessionStorage.removeItem('kin_plan');
  };

  const resetPassword = async (email) => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } };
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  };

  const getDailySearchCount = async (userId) => {
    if (!supabase) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { count } = await supabase
      .from('user_searches')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .gte('created_at', today.toISOString());
    return count || 0;
  };

  const recordSearch = async (userId, params) => {
    if (!supabase) return;
    await supabase.from('user_searches').insert({
      user_id: userId,
      product: params.hsDesc,
      origin_country: params.exporterName,
      destination_country: params.importerName,
      operation: params.tradeType,
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      userPlan,
      loading,
      fetchUserPlan,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      resetPassword,
      getDailySearchCount,
      recordSearch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
